import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, X, Trash2, Inbox } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { fetchSubmissions, updateDeal, deleteDeal } from "@/lib/api";

const STATUS = {
  pending: { label: "In Prüfung", cls: "bg-amber-50 text-amber-600" },
  posted: { label: "Veröffentlicht", cls: "bg-emerald-50 text-emerald-600" },
  saved: { label: "Abgelehnt", cls: "bg-rose-50 text-rose-600" },
};

export default function Submissions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    fetchSubmissions().then(setItems).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try {
      await updateDeal(id, { published: true, status: "posted" });
      toast.success("Deal freigegeben & veröffentlicht");
      load();
    } catch { toast.error("Freigabe fehlgeschlagen"); }
  };
  const reject = async (id) => {
    try {
      await updateDeal(id, { published: false, status: "saved" });
      toast.success("Deal abgelehnt");
      load();
    } catch { toast.error("Aktion fehlgeschlagen"); }
  };
  const remove = async (id) => {
    try {
      await deleteDeal(id);
      toast.success("Deal gelöscht");
      load();
    } catch { toast.error("Löschen fehlgeschlagen"); }
  };

  return (
    <div data-testid="submissions-page">
      <PageHeader title="Einreichungen" subtitle="Von Nutzern eingereichte Deals prüfen & freigeben" />
      <div className="px-6 md:px-10 py-8">
        {loading ? (
          <div className="flex justify-center py-24 text-[#86868B]"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-black/5">
            <Inbox className="h-12 w-12 mx-auto text-[#A1A1A6] mb-3" strokeWidth={1.5} />
            <p className="font-heading font-semibold text-[#1D1D1F]">Keine Einreichungen</p>
            <p className="text-sm text-[#86868B] mt-1">Von Nutzern eingereichte Deals erscheinen hier zur Freigabe.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="submissions-list">
            {items.map((d) => {
              const s = STATUS[d.status] || STATUS.pending;
              return (
                <div key={d.id} className="bg-white rounded-2xl border border-black/5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-4 flex items-center gap-4" data-testid={`submission-${d.id}`}>
                  <img src={d.image_url || "/wizard-icon.png"} alt="" className="h-16 w-16 rounded-xl object-cover bg-[#F5F5F7]" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1D1D1F] truncate">{d.product_name}</p>
                    <p className="text-sm text-[#86868B]">{d.deal_price.toFixed(2)} € · -{d.discount_percent}% · {d.source}</p>
                    <p className="text-xs text-[#A1A1A6] mt-0.5">von {d.submitter_name || "Unbekannt"}</p>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${s.cls}`}>{s.label}</span>
                  <div className="flex items-center gap-2">
                    {d.status !== "posted" && (
                      <Button size="sm" onClick={() => approve(d.id)} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`approve-${d.id}`}>
                        <Check className="h-4 w-4" /> Freigeben
                      </Button>
                    )}
                    {d.status === "pending" && (
                      <Button size="sm" variant="secondary" onClick={() => reject(d.id)} className="rounded-full" data-testid={`reject-${d.id}`}>
                        <X className="h-4 w-4" /> Ablehnen
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => remove(d.id)} className="rounded-full text-rose-600 hover:bg-rose-50" data-testid={`delete-${d.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
